import React, { useEffect, useRef, useState } from "react";
import { Box, Text } from "@chakra-ui/react";
import { FileUpload, FILE_UPLOAD_TYPES, UploadedFile } from "newui/components/FileUpload";
import { FileUploadRef } from "newui/components/FileUpload/types/FileUpload.types";
import { UploadStatus } from "newui/components/FileUpload/types/UploadStatus.type";
import { RfqDocumentCategorySelect } from "newui/components/ui/CommonSelects";
import { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import { CheckDuplicatesResult } from "../types/rfx-document.types";
import { SingleDuplicateResolution } from "./DuplicateFilenameModal";
import { BatchDuplicateResolutionModal, BatchResolution } from "./BatchDuplicateResolutionModal";

interface DocumentUploadSectionProps {
  categoryOptions: SelectOption[];
  duplicates: CheckDuplicatesResult["duplicates"];
  hasDuplicates: boolean;
  isCheckingDuplicates: boolean;
  /** True when editing an RFx Template (routes upload to the template handler). */
  isTemplate?: boolean;
  rfqId: number;
  /** Hard-delete the listed documents. Used by the batch-replace flow to
   * clear existing rows before the uploads land as new ones (per-file
   * replace_document_id isn't possible while FileUpload's extraData is
   * batch-scoped). */
  onBulkReplaceDelete: (docIds: number[]) => Promise<void>;
  onDuplicateModalClose: () => void;
  onDuplicateResolved: (resolutions: SingleDuplicateResolution | BatchResolution[]) => void;
  onFilesSelected: (files: File[]) => Promise<boolean>;
  onUploadComplete: (files: UploadedFile[]) => void;
  /** Fired whenever the in-flight upload count flips between 0 and >0. Lets
   * the parent hold the table in a loading state until every upload has
   * finished AND the table has refetched. */
  onUploadingChange?: (isUploading: boolean) => void;
}

export const DocumentUploadSection = ({
  rfqId,
  isTemplate = false,
  categoryOptions,
  hasDuplicates,
  duplicates,
  isCheckingDuplicates,
  onFilesSelected,
  onUploadComplete,
  onDuplicateResolved,
  onDuplicateModalClose,
  onBulkReplaceDelete,
  onUploadingChange,
}: DocumentUploadSectionProps) => {
  const uploadType = isTemplate
    ? FILE_UPLOAD_TYPES.RFQ_DOCUMENTATION_FILE_TEMPLATE_TYPE
    : FILE_UPLOAD_TYPES.RFQ_DOCUMENTATION_DOCUMENT_TYPE;
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

  // Default the category to the first option as soon as the list loads so the
  // user can drop files straight in without first having to pick a category
  // (and without ever seeing the "category required" warning).
  useEffect(() => {
    if (selectedCategoryId === null && categoryOptions.length > 0) {
      setSelectedCategoryId(Number(categoryOptions[0].value));
    }
  }, [categoryOptions, selectedCategoryId]);

  const handleCategoryChange = (option: SelectOption | SelectOption[] | null) => {
    if (option && !Array.isArray(option)) {
      setSelectedCategoryId(Number(option.value));
    } else {
      setSelectedCategoryId(null);
    }
  };

  const fileUploadRef = useRef<FileUploadRef | null>(null);

  // Promise resolver used to gate FileUpload while the user resolves a
  // duplicate-filename prompt. `null` outside of an active prompt.
  const pendingResolverRef = useRef<((files: File[]) => void) | null>(null);
  const pendingFilesRef = useRef<File[]>([]);

  // When the user chose "Replace existing" on the single-file duplicate
  // modal, we stash the target id + optional category/revision overrides
  // here and merge them into the FileUpload's extraData for the next batch.
  // Cleared once the upload completes.
  const [pendingReplace, setPendingReplace] = useState<{
    category_id?: number;
    replace_document_id: number;
    external_revision?: string;
  } | null>(null);

  // Per-file category map populated by the batch duplicate modal: filename →
  // chosen category id. Read inside `handleBeforeAccept` to attach per-file
  // `extraData` so each upload lands under the category the user picked for
  // it, even when the batch has files with different categories. Cleared once
  // the upload completes.
  const pendingPerFileCategoryRef = useRef<Record<string, number>>({});

  // Build the per-file extras shape FileUpload expects. We always attach
  // category_id (defaulting to the upload-section's selected category) so the
  // chunked uploader sees a deterministic value per file even when no
  // duplicate modal ran. Files that went through the duplicate modal
  // override that default via `pendingPerFileCategoryRef`.
  const enrichFiles = (files: File[]) => {
    const perFile = pendingPerFileCategoryRef.current;
    return files.map((f) => {
      const overridden = perFile[f.name];
      // Normalised match — chunked uploader stores names with spaces→_,
      // matching the FE check in checkDuplicateFilenames.
      const overriddenNormalised = perFile[f.name.replace(/ /g, "_")];
      const chosen = overridden ?? overriddenNormalised ?? selectedCategoryId;
      return {
        file: f,
        extraData: chosen !== null ? { category_id: chosen } : {},
      };
    });
  };

  const handleBeforeAccept = async (files: File[]) => {
    const hasDuplicates = await onFilesSelected(files);
    if (!hasDuplicates) return enrichFiles(files);
    // Park the files and wait for the modal flow to resolve.
    pendingFilesRef.current = files;
    return new Promise<{ extraData?: Record<string, any>; file: File; }[]>((resolve) => {
      pendingResolverRef.current = (resolved) => resolve(enrichFiles(resolved));
    });
  };

  const resolvePending = (files: File[]) => {
    const resolver = pendingResolverRef.current;
    pendingResolverRef.current = null;
    pendingFilesRef.current = [];
    resolver?.(files);
  };

  const handleChange = async (files: UploadedFile[]) => {
    // Notify the parent whenever the in-flight count changes so it can hold
    // the table in a loading state. "In flight" = IDLE (queued) or
    // UPLOADING — succeeded/errored rows don't count.
    const inFlight = files.some(
      (f) => f.status === UploadStatus.UPLOADING || f.status === UploadStatus.IDLE
    );
    onUploadingChange?.(inFlight);

    // Only fire onUploadComplete (which triggers a table refetch + provider
    // reload) when there's actually nothing in flight. Calling it on every
    // tick (IDLE → UPLOADING → UPLOADED) kept `isFetching` perpetually true
    // and the busy overlay never cleared.
    if (!inFlight) {
      onUploadComplete(files);
    }

    // Keep every card visible — succeeded, in-flight, and errored — while the
    // batch is still running. The user sees the full set of files they
    // dropped, with each row reporting its own progress/state, and nothing
    // jumps into the table mid-batch. Only once the whole batch has settled
    // do we sweep succeeded rows out (they'll reappear in the table via the
    // refetch above). ERROR rows still stay so the user can retry them.
    const isSucceeded = (f: UploadedFile) =>
      f.status === UploadStatus.UPLOADED || Boolean(f.id);

    if (!inFlight) {
      const succeeded = files.filter(isSucceeded);
      if (succeeded.length > 0) {
        // Defer to a microtask: `handleChange` runs synchronously from inside
        // `useFileUploadState.updateUploadedFile`'s `setFiles` updater, and a
        // nested `setFiles` (which is what `removeFile` calls under the hood)
        // is swallowed by React's batching. Queuing it lets the
        // upload-completed state commit first, then the removal runs as a
        // separate update.
        queueMicrotask(() => {
          succeeded.forEach((f) => {
            const id = f.id ?? f.uploadId;
            if (id !== undefined) fileUploadRef.current?.removeFile(id);
          });
        });
      }
    }

    // When the whole batch has settled (no IDLE/UPLOADING rows left), clear
    // the per-batch metadata so the next batch starts fresh.
    const allSettled = files.every(
      (f) => f.status !== UploadStatus.UPLOADING && f.status !== UploadStatus.IDLE
    );
    if (allSettled) {
      setPendingReplace(null);
      pendingPerFileCategoryRef.current = {};
    }
  };

  const handleBatchResolution = async (resolutions: BatchResolution[]) => {
    onDuplicateResolved(resolutions);

    // For every file the user marked "replace", hard-delete the existing
    // document (DB row + S3 object) before the upload proceeds. The new
    // upload then lands as a fresh row with the same file_name. Files
    // marked "keep" are left untouched and the new upload sits alongside
    // the existing one (true "keep both").
    //
    // Trade-off vs the single-file replace path: the existing row id and
    // `internal_revision` audit are lost. Preserving them would require
    // per-file extraData on FileUpload (currently batch-scoped).
    // Build the per-file category map so each incoming filename uploads
    // under the category the user picked for it in the modal.
    const perFile: Record<string, number> = {};
    resolutions.forEach((r) => {
      if (typeof r.incomingCategoryId === "number") {
        perFile[r.fileName] = r.incomingCategoryId;
      }
    });
    pendingPerFileCategoryRef.current = perFile;

    const replaceIds = resolutions
      .filter((r) => r.action === "replace")
      .map((r) => r.existingId);

    if (replaceIds.length > 0) {
      try {
        await onBulkReplaceDelete(replaceIds);
      } catch {
        // If the bulk delete fails, abort the upload — proceeding would
        // create new rows alongside the old ones (the "keeping all" bug
        // we're fixing here).
        resolvePending([]);
        return;
      }
    }

    resolvePending(pendingFilesRef.current);
  };

  const handleModalCancel = () => {
    onDuplicateModalClose();
    // Cancel the upload entirely when the user dismisses the prompt.
    resolvePending([]);
  };

  return (
    <Box data-testid="document-upload-section">
      <Text fontSize="lg" fontWeight="semibold" mb={4}>
        Upload Documents
      </Text>
      <FileUpload
        ref={fileUploadRef}
        multiple
        uploadType={uploadType}
        supportsHardDelete={false}
        disableAutoScroll
        onBeforeAccept={handleBeforeAccept}
        onChange={handleChange}
        extraData={{
          // Templates use the parallel chunked-upload handler keyed by
          // `rfx_template_id`; RFx still uses `rfq_id`. Sending the right
          // key is what tells the BE which table to insert into.
          ...(isTemplate ? { rfx_template_id: rfqId } : { rfq_id: rfqId }),
          // Default category — overridden per-file by `extraData` attached in
          // `enrichFiles` when the user picked a different one in the
          // duplicate modal.
          ...(selectedCategoryId !== null ? { category_id: selectedCategoryId } : {}),
          ...(pendingReplace ?? {}),
        }}
      >
        <Box
          mb={4}
          maxW="300px"
          mx="auto"
          // Stop clicks inside the category select from bubbling up to the
          // dropzone (which would open the OS file picker).
          onClick={(e) => e.stopPropagation()}
        >
          <RfqDocumentCategorySelect
            rfqId={rfqId}
            label="Document Category for Upload(s)"
            value={
              selectedCategoryId !== null
                ? categoryOptions.find((o) => o.value === selectedCategoryId) ?? null
                : null
            }
            onChange={handleCategoryChange}
            testId="upload-category-select"
          />
        </Box>
      </FileUpload>

      {/* Always use the batch resolution modal — even for a single duplicate
       * — so the UX is identical regardless of how many conflicts the user
       * is resolving. The single-file modal path is intentionally dead. */}
      {hasDuplicates && (
        <BatchDuplicateResolutionModal
          isOpen={hasDuplicates}
          duplicates={duplicates}
          categoryOptions={categoryOptions}
          defaultCategoryId={selectedCategoryId}
          onConfirm={handleBatchResolution}
          onClose={handleModalCancel}
        />
      )}
    </Box>
  );
};
